/**
 * Centralized API endpoint paths for the Vendor portal.
 *
 * Paths are RELATIVE to `VITE_API_BASE_URL` (which includes the `/api/v1` prefix).
 */
export const endpoints = {
  auth: {
    login: "/auth/login",
    registerVendor: "/auth/register/vendor",
    verifyEmail: "/auth/verify-email",
    resendOtp: "/auth/resend-otp",
    refresh: "/auth/refresh",
    logout: "/auth/logout",
    forgotPassword: "/auth/forgot-password",
    resetPassword: "/auth/reset-password",
    changePassword: "/auth/change-password",
    contextSwitch: "/auth/context/switch",
    contexts: "/auth/contexts",
    me: "/auth/me",
  },
  onboarding: {
    overview: "/vendor/onboarding",
    profile: "/vendor/onboarding/profile",
    submit: "/vendor/onboarding/submit",
    status: "/vendor/onboarding/status",
  },
  files: {
    upload: "/files",
    detail: (fileId: string) => `/files/${fileId}`,
  },
  documents: {
    list: "/vendor/documents",
    create: "/vendor/documents",
  },
  approvals: {
    list: "/vendor/approvals",
    detail: (approvalId: string) => `/vendor/approvals/${approvalId}`,
  },
  geography: {
    countries: "/vendor/geography/countries",
    country: (countryId: string) => `/vendor/geography/countries/${countryId}`,
    municipalities: "/vendor/geography/municipalities",
    municipality: (municipalityId: string) => `/vendor/geography/municipalities/${municipalityId}`,
    districts: "/vendor/geography/districts",
    district: (districtId: string) => `/vendor/geography/districts/${districtId}`,
  },
  coverage: {
    organization: "/vendor/coverage",
    overview: "/vendor/coverage/overview",
  },
  catalog: {
    services: "/vendor/catalog/services",
    service: (serviceId: string) => `/vendor/catalog/services/${serviceId}`,
    serviceOptions: "/vendor/catalog/service-options",
    serviceOption: (optionId: string) => `/vendor/catalog/service-options/${optionId}`,
  },
  services: {
    list: "/vendor/services",
    available: "/vendor/services/available",
    detail: (vendorServiceId: string) => `/vendor/services/${vendorServiceId}`,
    readiness: (vendorServiceId: string) => `/vendor/services/${vendorServiceId}/readiness`,
    submitActivation: (vendorServiceId: string) =>
      `/vendor/services/${vendorServiceId}/submit-activation`,
    coverage: (vendorServiceId: string) => `/vendor/services/${vendorServiceId}/coverage`,
    effectiveCoverage: (vendorServiceId: string) =>
      `/vendor/services/${vendorServiceId}/effective-coverage`,
  },
  assets: {
    trucks: "/vendor/assets/trucks",
    truck: (truckId: string) => `/vendor/assets/trucks/${truckId}`,
    containers: "/vendor/assets/containers",
    container: (containerId: string) => `/vendor/assets/containers/${containerId}`,
    drivers: "/vendor/assets/drivers",
    driver: (driverId: string) => `/vendor/assets/drivers/${driverId}`,
  },
  orders: {
    list: "/vendor/orders",
    detail: (orderId: string) => `/vendor/orders/${orderId}`,
    accept: (orderId: string) => `/vendor/orders/${orderId}/accept`,
    reject: (orderId: string) => `/vendor/orders/${orderId}/reject`,
    rejectionReasons: "/vendor/rejection-reasons",
  },
  workOrders: {
    list: "/vendor/work-orders",
    create: "/vendor/work-orders",
    detail: (workOrderId: string) => `/vendor/work-orders/${workOrderId}`,
    assign: (workOrderId: string) => `/vendor/work-orders/${workOrderId}/assign`,
    reschedule: (workOrderId: string) => `/vendor/work-orders/${workOrderId}/reschedule`,
    start: (workOrderId: string) => `/vendor/work-orders/${workOrderId}/start`,
    proofs: (workOrderId: string) => `/vendor/work-orders/${workOrderId}/proofs`,
    complete: (workOrderId: string) => `/vendor/work-orders/${workOrderId}/complete`,
  },
  deliveryTimeSlots: {
    public: "/public/delivery-time-slots",
  },
  biddingRoom: {
    list: "/vendor/bidding-room",
    detail: (requestId: string) => `/vendor/bidding-room/${requestId}`,
    submitBid: (requestId: string) => `/vendor/bidding-room/${requestId}/bid`,
    reviseBid: (requestId: string) => `/vendor/bidding-room/${requestId}/bid`,
    withdrawBid: (requestId: string) => `/vendor/bidding-room/${requestId}/bid/withdraw`,
  },
  bids: {
    list: "/vendor/bids",
  },
  pricing: {
    upsert: (vendorServiceId: string) => `/vendor/services/${vendorServiceId}/pricing`,
    bulk: (vendorServiceId: string) => `/vendor/services/${vendorServiceId}/pricing/bulk`,
    versions: (vendorServiceId: string) => `/vendor/services/${vendorServiceId}/pricing/versions`,
  },
  notifications: {
    list: "/notifications",
    markRead: (notificationId: string) => `/notifications/${notificationId}/read`,
  },
} as const;
